// Content for the "esg-fullday-practitioner" course slot. This deck was
// previously a condensed adaptation of the reading-course text in
// server.js; it has been replaced with a faithful transcription of the
// real, already-delivered PowerPoint deck for Okiru's full ESG workshop:
// "ESG Mastery: The Comprehensive Deep Dive" (61 source slides, seven
// parts — Part 0 The Case for ESG, Part 1 Foundations, Part 2 Frameworks,
// Part 3 Jurisdictional Detail, Part 4 Company Application, Part 5
// Technical Implementation, Part 6 Assessment & Roadmap). Transcribed
// from the extracted slide-text JSON and restructured into this deck's
// JSON slide schema — every framework name, statistic and claim below is
// carried over from that source deck as-is; nothing here is invented, and
// nothing is hedged beyond what the source itself states.
//
// manifest.id stays 'esg-fullday-practitioner' because server.js loads
// this deck by that id.
//
// Module indices below run 1-7, not the source deck's own Part 0-6
// numbering — purely so a slide's `module` value is never the number 0,
// which SlideRenderer's IntroLayout (`String(s.module).padStart(...)`)
// and PresenterView (`if (!slide?.module) return null`) would otherwise
// treat as falsy. The kickers and titles on every slide still read
// "Part 0", "Part 1" etc. exactly as the source deck does.

import { applyOkiruTemplate } from '../okiruPresentationTemplate';

export const manifest = applyOkiruTemplate({
  id: 'esg-fullday-practitioner',
  title: 'ESG Mastery: The Comprehensive Deep Dive',
  subtitle: "Full workshop format · seven parts, from why it matters to how it's done",
  modules: [
    { index: 1, name: 'Part 0: The Case for ESG' },
    { index: 2, name: 'Part 1: Foundations' },
    { index: 3, name: 'Part 2: Frameworks' },
    { index: 4, name: 'Part 3: Jurisdictional Detail' },
    { index: 5, name: 'Part 4: Company Application' },
    { index: 6, name: 'Part 5: Technical Implementation' },
    { index: 7, name: 'Part 6: Assessment & Roadmap' },
  ],
}, 'okiru-light');

export const slides = [
  {
    id: 1, layout: 'title',
    kicker: 'FULL WORKSHOP · COMPREHENSIVE',
    title: 'ESG Mastery: The Comprehensive Deep Dive',
    lead: 'A grounded, certified-expert-level session: the African business case for ESG, every major framework in technical depth, a full worked materiality case study, GHG accounting mechanics, supply chain due diligence, and a knowledge check.',
    meta: ["Full workshop format · seven parts, from why it matters to how it's done"],
  },
  {
    id: 2, layout: 'agenda',
    kicker: 'AGENDA', title: 'Deep Dive Agenda: Seven Parts',
    lead: 'Opens with the business case for ESG in Africa and its intersection with B-BBEE, then moves through six parts of regulatory depth, GHG mechanics, and a knowledge check.',
    columns: [
      [
        { time: 'Part 0', label: 'The Case for ESG — the African business case and its intersection with B-BBEE.' },
        { time: 'Part 1', label: 'Foundations — ESG definitions, materiality, and the regulatory landscape.' },
        { time: 'Part 2', label: 'Frameworks — GRI, SASB, ISSB, TCFD, and CSRD compared in depth.' },
        { time: 'Part 3', label: 'Jurisdictional Detail — how adoption actually varies by region and what it means for you.' },
      ],
      [
        { time: 'Part 4', label: 'Company Application — materiality assessment, governance design, and target-setting.' },
        { time: 'Part 5', label: 'Technical Implementation — GHG accounting mechanics, supply chain due diligence, data systems.' },
        { time: 'Part 6', label: 'Assessment & Roadmap — knowledge check, capstone roadmap exercise, and certification prep.' },
      ],
    ],
  },
  {
    id: 3, layout: 'numbered',
    kicker: 'AGENDA', title: 'Learning Objectives',
    lead: 'By the end of this session, you will be able to:',
    items: [
      { title: 'Materiality', description: 'Explain the difference between financial, impact, and double materiality, and apply each correctly.' },
      { title: 'Framework comparison', description: 'Compare GRI, SASB, ISSB (IFRS S1/S2), TCFD, and CSRD/ESRS on scope, audience, and disclosure basis.' },
      { title: 'Jurisdictional exposure', description: 'Map jurisdictional exposure and determine where mandatory disclosure obligations apply.' },
      { title: 'Materiality assessment', description: 'Conduct a basic materiality assessment for a company and prioritize ESG topics.' },
      { title: 'Governance design', description: 'Design an ESG governance structure with clear roles, escalation paths, and board oversight.' },
      { title: 'GHG calculation', description: 'Perform a basic GHG emissions calculation and explain organizational/operational boundaries.' },
      { title: 'Data and assurance', description: 'Identify data sources, gaps, and assurance requirements for credible disclosure.' },
      { title: 'Roadmap building', description: 'Build a phased ESG implementation roadmap appropriate to organizational maturity.' },
    ],
  },

  // ---------- Part 0: The Case for ESG ----------
  {
    id: 4, layout: 'intro', module: 1,
    kicker: 'PART 0', title: 'The Case for ESG: Global Stakes, African Reality',
    lead: 'Grounding the frameworks in the numbers, risks, and opportunities that make ESG unavoidable',
    outcome: 'Why ESG is unavoidable for African businesses right now, and where B-BBEE and ESG intersect.',
    templateAsset: 'workshop-facilitator', imageChrome: 'none',
  },
  {
    id: 5, layout: 'cards', module: 1,
    kicker: 'PART 0', title: 'Four Forces Making ESG Unavoidable',
    lead: 'Capital, regulation, climate, and trust are converging — none of them wait for a company to be ready.',
    columns: 4,
    cards: [
      { title: 'Capital markets', description: 'Sustainability-linked loans, green bonds, and ESG-screened funds now price risk into the cost of capital itself.' },
      { title: 'Tightening regulation', description: 'ISSB, CSRD, and JSE listing requirements are moving disclosure from voluntary to mandatory.' },
      { title: 'Physical & transition risk', description: 'Climate impacts and the shift away from carbon-intensive activity are already reshaping costs and markets.' },
      { title: 'Trust deficit', description: 'Governance failures and greenwashing scandals have made credible disclosure a condition of investor confidence.' },
    ],
  },
  {
    id: 6, layout: 'comparison', module: 1,
    kicker: 'PART 0', title: "ESG by the Numbers: Africa's Reality",
    lead: 'Three pillars, three very different — and very local — stories.',
    columns: [
      { heading: 'Environmental', items: [
        '2–3% of global emissions come from here',
        '7 of 10 most vulnerable nations',
        '$65bn coastal damage risk by 2050',
        "60% of world's best solar potential",
      ] },
      { heading: 'Social', items: [
        '33.2% SA unemployment (Q2 2025)',
        '46.1% youth unemployment',
        '64% of Black SA in poverty',
        'vs. 1% of White South Africans',
      ] },
      { heading: 'Governance', items: [
        '32/100 avg. CPI score',
        'Lowest-performing region globally',
        'SA scores 41/100 (rank 81 of 180)',
        'R123bn lost in Steinhoff collapse',
      ] },
    ],
  },
  {
    id: 7, layout: 'cards', module: 1,
    kicker: 'PART 0 · ENVIRONMENTAL', title: 'Environmental: Least Responsible, Most Exposed',
    lead: 'Africa contributes almost nothing to the problem, carries a disproportionate share of the damage, and holds the resources to lead the solution.',
    columns: 3,
    cards: [
      { title: '2–3%', description: "Africa's share of global energy and industrial CO2 emissions, despite hosting nearly 20% of the world's population." },
      { title: '7 of 10', description: 'Of the most climate-vulnerable countries worldwide are on the African continent.' },
      { title: '60%', description: "Of the world's best solar resources sit in Africa, alongside $34bn already invested in renewables since 2020." },
    ],
    footer: 'Practical implication: for African businesses, the environmental case for ESG is as much about opportunity capture as risk management.',
  },
  {
    id: 8, layout: 'cards', module: 1,
    kicker: 'PART 0 · SOCIAL', title: 'Social: Inequality Is the Baseline, Not the Exception',
    lead: 'For African businesses, social materiality starts with labour markets and living standards that are already under severe strain.',
    columns: 3,
    cards: [
      { title: '33.2%', description: "South Africa's unemployment rate (Q2 2025); the expanded rate, including discouraged jobseekers, is 43.1%." },
      { title: '46.1%', description: 'Youth unemployment nationally, with some provinces above 50% (Eastern Cape 54.3%, North West 58.8%).' },
      { title: '64% vs 1%', description: 'Share of Black South Africans living in poverty, compared with just 1% of White South Africans.' },
    ],
    footer: 'Practical implication: labour, health, and safety metrics carry real weight here — social materiality is not an abstraction.',
  },
  {
    id: 9, layout: 'lead', module: 1,
    kicker: 'PART 0 · GOVERNANCE', title: "Governance: Africa's Trust Gap",
    lead: 'Weak institutions raise the cost of capital for everyone — and one governance failure can erase decades of value in days.',
    body: 'The Institutional Picture: 32/100 average Corruption Perceptions Index score across Sub-Saharan Africa — the lowest-performing region globally. Only 4 of the 49 countries assessed score above 50, the threshold for relatively strong public-sector integrity. South Africa scores 41/100, ranked 81st of 180 countries (2025). Weak governance is priced into the cost of capital, whether or not it is formally disclosed. Note: institutional weakness is a business risk, not just a public-sector one.',
    templateAsset: 'icon-consistency', imageChrome: 'none',
  },
  {
    id: 10, layout: 'timeline', module: 1,
    kicker: 'PART 0 · GOVERNANCE', title: 'When Governance Fails: Steinhoff',
    lead: 'One governance failure can erase decades of value in days.',
    steps: [
      { era: 'December 2017', title: 'Accounting irregularities disclosed', description: "Steinhoff's board disclosed \"accounting irregularities\" after auditors refused to sign off." },
      { era: 'Days later', title: 'Share price collapses', description: 'The share price collapsed more than 95% within days — around R123bn (€6.5bn) in overstated profits and assets.' },
      { era: 'Aftermath', title: 'Pension funds hit', description: "Pension funds, including South Africa's Government Employees Pension Fund, lost billions." },
      { era: 'Root cause', title: 'Board oversight failure', description: 'A board that failed to exercise independent oversight over management.' },
    ],
  },
  {
    id: 11, layout: 'cards', module: 1,
    kicker: 'PART 0', title: 'So Why Try? The ESG Opportunity',
    lead: 'The same numbers that make the case uncomfortable also make the case for moving first.',
    columns: 4,
    cards: [
      { title: 'Cheaper capital', description: 'Sustainability-linked loans and green bonds are already pricing strong ESG performance into lower borrowing costs.' },
      { title: 'A buildout already under way', description: '$34bn invested in African renewables since 2020, with targets moving from 56GW (2022) toward 300GW by 2030.' },
      { title: 'A young, digitally fluent workforce', description: 'A large youth population is a genuine advantage in building the skills a green and digital economy needs.' },
      { title: 'First-mover credibility', description: 'Firms with measurable ESG performance are turning it into a commercial differentiator, not just a compliance cost.' },
    ],
  },
  {
    id: 12, layout: 'cards', module: 1,
    kicker: 'PART 0', title: 'African ESG in Action',
    lead: 'Three examples of ESG moving from slide deck to balance sheet on the continent.',
    columns: 3,
    cards: [
      { title: 'Safaricom (Kenya)', description: 'FY2025: $7.3bn total societal impact reported, a KES 30bn sustainability-linked loan, and a net-zero-by-2050 commitment.' },
      { title: 'Sasol, Anglo American & De Beers (SA)', description: 'Feb 2025: joint development agreement to pilot renewable diesel feedstock, decarbonising hard-to-abate operations.' },
      { title: "South Africa's renewables pipeline", description: "South Africa leads Africa's renewable investment pipeline, with projects like the 420MW Koruson 1 wind cluster now operational." },
    ],
  },
  {
    id: 13, layout: 'lead', module: 1,
    kicker: 'PART 0', title: 'Where B-BBEE and ESG Intersect',
    lead: 'For South African organisations, these are not two separate compliance programmes — they measure much of the same transformation.',
    templateAsset: 'team-collaboration', imageChrome: 'none',
  },
  {
    id: 14, layout: 'comparison', module: 1,
    kicker: 'PART 0', title: 'The Same Transformation, Two Scorecards',
    lead: 'Map the seven B-BBEE elements onto E, S, and G, and both the overlap and the gap become obvious.',
    columns: [
      { heading: 'Environmental', items: [
        'Not a B-BBEE element at all',
        'Outside the B-BBEE scorecard entirely',
        'A blind spot in B-BBEE-only reporting',
        'Where the two frameworks diverge most',
      ] },
      { heading: 'Social', items: [
        'Ownership → economic inclusion',
        'Skills Dev. → workforce investment',
        'ESD → economic inclusion',
        'SED → community impact',
      ] },
      { heading: 'Governance', items: [
        'Mgmt Control → board oversight',
        'Procurement → supply chain oversight',
        'Fronting rules → anti-corruption',
        'Verification → assurance discipline',
      ] },
    ],
  },
  {
    id: 15, layout: 'comparison', module: 1,
    kicker: 'PART 0 · B-BBEE & ESG', title: 'Same Data, Different Lens',
    lead: 'Most of what ESG frameworks ask for, B-BBEE-verified companies are already collecting — the work is mapping it, not duplicating it.',
    columns: [
      { heading: 'What B-BBEE Already Measures', items: [
        'Skills Development spend as a % of payroll, independently verified every year.',
        'Board and executive demographic representation against set targets.',
        'Socio-Economic Development spend on qualifying beneficiaries.',
        'Preferential Procurement spend from black-owned and black women-owned suppliers.',
      ] },
      { heading: 'What ESG Frameworks Ask For', items: [
        'GRI 404 training and development disclosures; ISSB workforce metrics.',
        'GRI 405 diversity disclosures; King IV and CSRD board oversight requirements.',
        'GRI 203 indirect economic impacts; community investment reporting.',
        'GRI/CSRD supply chain due diligence; local and inclusive procurement metrics.',
      ] },
    ],
    callout: { label: 'Note', text: 'This data already exists — most of it just needs relabelling.' },
  },
  {
    id: 16, layout: 'cards', module: 1,
    kicker: 'PART 0 · B-BBEE & ESG', title: 'Where They Diverge',
    lead: 'Treating one as a substitute for the other is the most common mistake in this space.',
    columns: 4,
    cards: [
      { title: 'No environmental coverage', description: 'B-BBEE scores ownership, skills, and procurement — it has no climate, water, waste, or biodiversity metric at all.' },
      { title: 'Race-based vs. universal DEI', description: "B-BBEE targets are built around South Africa's racial categories; global ESG frameworks assess broader DEI, safety, and human rights." },
      { title: 'Legislative teeth vs. investor pull', description: 'B-BBEE compliance is enforced through law, fronting penalties, and tender access; ESG disclosure is increasingly investor- and regulator-driven.' },
      { title: 'South Africa-only vs. global audience', description: 'A B-BBEE certificate has no currency outside South Africa; ISSB, GRI, and CSRD are read by international investors and lenders.' },
    ],
  },
  {
    id: 17, layout: 'cards', module: 1,
    kicker: 'PART 0 · B-BBEE & ESG', title: 'The Integration Opportunity',
    lead: 'Treating B-BBEE and ESG as one transformation programme, not two, is both more efficient and more credible.',
    columns: 3,
    cards: [
      { title: 'One committee', description: 'The Social & Ethics Committee, mandatory under the Companies Act, is the natural governance home for both.' },
      { title: 'One data architecture', description: 'Build a single data system feeding both your B-BBEE scorecard and your ESG disclosures.' },
      { title: 'One disclosure calendar', description: 'Align your B-BBEE verification cycle with your ESG reporting deadlines to cut duplicated audit effort.' },
    ],
    footer: 'Practical implication: the companies doing this well publish one transformation and sustainability narrative, not two separate reports.',
  },
  {
    id: 18, layout: 'cards', module: 1,
    kicker: 'PART 0', title: 'From Context to Competence',
    lead: 'The stakes above are real and immediate. What follows is the toolkit that turns them into a credible, assured ESG programme.',
    columns: 3,
    cards: [
      { title: 'Foundations & Frameworks', description: 'Shared definitions, materiality, and how GRI, SASB, ISSB, TCFD, and CSRD relate to one another.' },
      { title: 'Company Application', description: 'Scoping, materiality assessment, governance design, and target-setting for a real organisation.' },
      { title: 'Implementation & Assurance', description: 'Data infrastructure, GHG accounting mechanics, supply chain due diligence, and audit readiness.' },
    ],
  },

  // ---------- Part 1: Foundations ----------
  {
    id: 19, layout: 'intro', module: 2,
    kicker: 'PART 1', title: 'Foundations: Defining ESG & Materiality',
    lead: 'Establishing a shared, precise vocabulary before moving into frameworks and company application.',
    outcome: 'A working definition of ESG, and the difference between financial, impact, and double materiality.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 20, layout: 'comparison', module: 2,
    kicker: 'PART 1', title: 'ESG: A Working Definition',
    lead: 'ESG is a structured framework for identifying, measuring, and disclosing non-financial factors that affect long-term enterprise value and stakeholder outcomes.',
    columns: [
      { heading: 'Environmental', items: ['Climate & emissions', 'Energy & resource use', 'Water & waste', 'Biodiversity'] },
      { heading: 'Social', items: ['Labor & human rights', 'DEI', 'Health & safety', 'Community impact'] },
      { heading: 'Governance', items: ['Board oversight', 'Ethics & anti-corruption', 'Risk management', 'Executive pay'] },
    ],
  },
  {
    id: 21, layout: 'comparison', module: 2,
    kicker: 'PART 1', title: 'Materiality: The Filtering Mechanism',
    lead: 'Without materiality, ESG reporting becomes an unfocused list of every possible issue. Materiality answers: what matters, to whom, and why?',
    columns: [
      { heading: 'Financial Materiality', items: [
        "Question: Could this issue reasonably affect the company's cash flows, access to finance, or cost of capital?",
        'Perspective: outside-in (how the world affects the company)',
        'Frameworks: ISSB (IFRS S1/S2), SASB, TCFD',
        'Primary audience: investors, lenders, creditors',
        'Note: this is the sole basis IFRS S1 uses.',
      ] },
      { heading: 'Impact Materiality', items: [
        "Question: Does the company's activity have a significant actual or potential effect on people or the environment?",
        'Perspective: inside-out (how the company affects the world)',
        'Frameworks: GRI; combined with financial materiality under CSRD/ESRS',
        'Primary audience: broader stakeholders (communities, NGOs, workers)',
      ] },
    ],
  },
  {
    id: 22, layout: 'keyterm', module: 2,
    kicker: 'PART 1', term: 'Double Materiality',
    definition: 'CSRD/ESRS formally combines both lenses — an issue is material if either threshold is met (Financial Materiality × Impact Materiality = Double Material).',
    body: 'An issue only needs to clear one of the two thresholds — financial or impact — to be double material under CSRD/ESRS.',
    templateAsset: 'problem-solving',
    callouts: [
      { label: 'Example: GHG emissions', text: 'Financially material (transition risk, carbon pricing exposure) AND impact-material (contributes to climate change) — clearly reportable under CSRD.' },
      { label: 'Example: local biodiversity loss', text: 'May be impact-material without being clearly financially material today — still reportable under double materiality, but not under IFRS S1/S2 alone.' },
    ],
  },
  {
    id: 23, layout: 'cards', module: 2,
    kicker: 'PART 1', title: 'Identifying Stakeholders for Materiality Input',
    lead: 'A credible materiality assessment draws on structured input from a defined stakeholder set.',
    columns: 3,
    cards: [
      { title: 'Investors & lenders', description: 'Cost of capital, risk-adjusted returns, portfolio screening criteria.' },
      { title: 'Employees', description: 'Working conditions, safety, DEI, career development.' },
      { title: 'Customers & suppliers', description: 'Product responsibility, supply chain standards, contractual requirements.' },
      { title: 'Regulators & policymakers', description: 'Legal compliance, disclosure mandates, sector-specific rules.' },
      { title: 'Communities & NGOs', description: 'Local environmental and social impact, license to operate.' },
      { title: 'Board & leadership', description: 'Strategic risk appetite, enterprise risk integration.' },
    ],
  },
  {
    id: 24, layout: 'numbered', module: 2,
    kicker: 'PART 1', title: 'The Materiality Matrix',
    lead: 'A common output of the assessment process — plotting issues by significance to stakeholders vs. significance to the business. X axis: significance to stakeholders. Y axis: significance to business/investors.',
    items: [
      { title: 'Top-right quadrant', description: 'Priority disclosure topics — material to both lenses.' },
      { title: 'Bottom-left quadrant', description: "Lower priority for now — monitor, don't ignore." },
      { title: 'Top-left / bottom-right quadrants', description: 'Divergence between lenses — worth board discussion.' },
    ],
  },
  {
    id: 25, layout: 'timeline', module: 2,
    kicker: 'WORKED CASE STUDY', title: 'Case Study: Materiality Assessment Walkthrough',
    lead: 'A generic mid-cap logistics company works through the five-step process from start to finish.',
    steps: [
      { era: 'Long-list', title: 'Long-list', description: "Started with 32 candidate topics drawn from GRI Sector Standards for logistics and SASB's road transportation standard." },
      { era: 'Stakeholder input', title: 'Stakeholder input', description: 'Surveyed investors, large shippers (customers), drivers, and two NGOs focused on road safety and emissions.' },
      { era: 'Scoring', title: 'Scoring', description: 'Each topic scored 1-10 on both financial and impact significance; driver safety and fleet emissions scored highest on both axes.' },
      { era: 'Matrix outcome', title: 'Matrix outcome', description: '12 topics landed in the top-right quadrant: fleet emissions, driver safety, fuel cost exposure, data security, and others.' },
      { era: 'Validation', title: 'Validation', description: 'Findings presented to the executive team; two topics were reprioritized after CFO input on insurance cost trends.' },
      { era: 'Outcome', title: 'Outcome', description: "The 12 material topics became the basis for the company's first ISSB-aligned disclosure and KPI set." },
    ],
  },
  {
    id: 26, layout: 'exercise', module: 2,
    kicker: 'WORKSHOP EXERCISE', meta: '15 min · small groups', title: 'Exercise: Rank These Issues',
    steps: [
      { title: 'GHG emissions' },
      { title: 'Employee turnover' },
      { title: 'Water usage at facilities' },
      { title: 'Board diversity' },
      { title: 'Data privacy' },
      { title: 'Supply chain labor standards' },
      { title: 'Packaging waste' },
      { title: 'Executive compensation structure' },
    ],
    checkFor: 'in small groups, plot each of the eight issues above on the materiality matrix for a generic mid-size manufacturing company.',
  },

  // ---------- Part 2: Frameworks ----------
  {
    id: 27, layout: 'intro', module: 3,
    kicker: 'PART 2', title: 'The Framework Landscape in Depth',
    lead: 'Understanding how GRI, SASB, ISSB, TCFD, and CSRD relate, overlap, and differ — and when each applies.',
    outcome: 'How to select and combine frameworks for a real reporting scenario, not just recite their names.',
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 28, layout: 'comparison', module: 3,
    kicker: 'PART 2 · GRI', title: 'GRI: The Global Reporting Initiative',
    lead: 'The most widely used sustainability reporting framework worldwide, built on impact materiality.',
    columns: [
      { heading: 'Key facts', items: [
        'Founded 1997; the longest-standing global sustainability standard.',
        'Organized into Universal, Sector, and Topic Standards.',
        'Voluntary in most jurisdictions, though some countries reference it in policy.',
        'Designed for broad stakeholder accountability, not only investors.',
      ] },
      { heading: 'When to use GRI', items: [
        'Broad stakeholder communication (sustainability report, NGOs, communities)',
        'Companies with significant operational footprint or impact on local communities',
        'Organizations wanting a comprehensive, topic-based reporting structure',
        'Often used alongside ISSB/CSRD, not as a replacement',
      ] },
    ],
  },
  {
    id: 29, layout: 'cards', module: 3,
    kicker: 'PART 2 · SASB', title: 'SASB: Industry-Specific, Investor-Focused',
    lead: 'The Sustainability Accounting Standards Board built financially material, industry-specific metrics — now part of the ISSB.',
    columns: 3,
    cards: [
      { title: '77 industry standards', description: 'Each industry has its own set of financially material disclosure topics and metrics.' },
      { title: 'Financial materiality only', description: 'Built explicitly for investor decision-making, not broad stakeholder reporting.' },
      { title: 'Now part of ISSB', description: "SASB Standards were consolidated into the IFRS Foundation and underpin IFRS S2's industry-based guidance." },
    ],
  },
  {
    id: 30, layout: 'comparison', module: 3,
    kicker: 'PART 2 · ISSB', title: 'ISSB: IFRS S1 & S2 — The Emerging Global Baseline',
    lead: 'Issued in June 2023, now the fastest-growing global disclosure standard, integrating TCFD and SASB.',
    columns: [
      { heading: 'IFRS S1', items: ['General requirements for disclosing all material sustainability-related risks and opportunities affecting enterprise value.'] },
      { heading: 'IFRS S2', items: ['Climate-specific disclosures: GHG emissions, physical & transition risks, scenario analysis. Fully integrates TCFD.'] },
    ],
    callout: { label: 'Shared foundation (from TCFD)', text: 'Both standards share four core content pillars: Governance, Strategy, Risk Management, and Metrics & Targets.' },
    footnote: 'Status: adopted or being adopted in 28+ jurisdictions (50%+ of global GDP) as of April 2026.',
  },
  {
    id: 31, layout: 'cards', module: 3,
    kicker: 'PART 2 · TCFD', title: 'TCFD: Still the Conceptual Backbone',
    lead: 'The Task Force on Climate-related Financial Disclosures is technically "retired" as a standalone body, but its structure lives on inside IFRS S2.',
    columns: 4,
    cards: [
      { title: 'Governance', description: 'Board and management oversight of climate-related risks and opportunities.' },
      { title: 'Strategy', description: 'Actual and potential impacts of climate risk on business, strategy, and financial planning.' },
      { title: 'Risk Management', description: 'Processes for identifying, assessing, and managing climate-related risk.' },
      { title: 'Metrics & Targets', description: 'Metrics used to assess and manage climate risk, including emissions data.' },
    ],
    footer: 'Practical note: if a company has already reported under TCFD, the transition to IFRS S2 is comparatively straightforward.',
  },
  {
    id: 32, layout: 'lead', module: 3,
    kicker: 'PART 2 · CSRD', title: "CSRD / ESRS: The EU's Mandatory Standard",
    lead: 'The Corporate Sustainability Reporting Directive requires detailed, assured, double-materiality-based disclosure for in-scope companies.',
    body: 'Applies to large EU undertakings and EU-listed companies; also reaches non-EU parents with sufficient EU activity. Uses European Sustainability Reporting Standards (ESRS), developed by EFRAG. Built on double materiality — both financial and impact materiality must be assessed.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 33, layout: 'keyterm', module: 3,
    kicker: 'PART 2 · CSRD', term: 'CSRD Scope Is Evolving',
    definition: "On 24 February 2026, EU ministers approved reducing CSRD's scope as part of a broader simplification effort.",
    body: 'EFRAG submitted draft ESRS amendments (Dec 2025) to improve interoperability with ISSB standards.',
    templateAsset: 'problem-solving',
    callouts: [
      { label: 'Implication', text: 'CSRD scope and detail are evolving — track EFRAG and EU Commission updates closely if you are in scope.' },
    ],
  },
  {
    id: 34, layout: 'cards', module: 3,
    kicker: 'PART 2', title: 'Framework Comparison at a Glance',
    lead: "A practitioner's reference table for selecting the right framework(s) — scope, materiality basis, and primary audience.",
    columns: 5,
    cards: [
      { title: 'GRI', description: 'Scope: global, voluntary. Materiality basis: impact materiality. Primary audience: broad stakeholders.' },
      { title: 'SASB', description: 'Scope: industry-specific. Materiality basis: financial materiality. Primary audience: investors.' },
      { title: 'ISSB (IFRS S1/S2)', description: 'Scope: global baseline. Materiality basis: financial materiality. Primary audience: investors, lenders.' },
      { title: 'TCFD', description: 'Scope: climate-specific (folded into IFRS S2). Materiality basis: financial materiality. Primary audience: investors.' },
      { title: 'CSRD / ESRS', description: 'Scope: EU mandatory. Materiality basis: double materiality. Primary audience: investors + stakeholders.' },
    ],
  },
  {
    id: 35, layout: 'cards', module: 3,
    kicker: 'PART 2 · INTEROPERABILITY', title: 'Interoperability: You Rarely Choose Just One',
    lead: 'Most multinational companies report under a combination of frameworks, mapped to avoid duplicate data collection.',
    columns: 3,
    cards: [
      { title: 'EU-listed multinational', description: 'CSRD/ESRS (mandatory) + ISSB-aligned disclosure for non-EU investors, with GRI for broader stakeholder reporting.' },
      { title: 'US-listed company with EU operations', description: 'SEC disclosure requirements + ISSB voluntary alignment, monitoring CSRD applicability through EU subsidiaries.' },
      { title: 'Emerging market exporter', description: 'ISSB as the baseline (per local roadmap) + GRI to meet international buyer expectations.' },
    ],
  },

  // ---------- Part 3: Jurisdictional Detail ----------
  {
    id: 36, layout: 'intro', module: 4,
    kicker: 'PART 3', title: 'Jurisdictional Detail: Where the Rules Actually Apply',
    lead: 'Global frameworks matter in the abstract — but compliance obligations are set jurisdiction by jurisdiction. This section grounds the picture in specifics.',
    outcome: "How to map your own jurisdictional exposure, including CSRD's extraterritorial reach.",
    templateAsset: 'thinking-learner', imageChrome: 'none',
  },
  {
    id: 37, layout: 'cards', module: 4,
    kicker: 'PART 3', title: 'Global ISSB Adoption: A Snapshot',
    lead: 'Adoption is accelerating but highly uneven in timing and mandatory status.',
    columns: 4,
    cards: [
      { title: 'European Union', description: 'CSRD/ESRS is the binding regime; scope reduced via Feb 2026 simplification. ISSB interoperability improving via EFRAG amendments.' },
      { title: 'United Kingdom', description: 'UK-endorsed ISSB-aligned standards in progress; mandatory disclosure for large companies expected from 2026 reporting periods.' },
      { title: 'United States', description: 'SEC climate rules and state-level regimes (e.g. California SB 261) operate alongside voluntary ISSB alignment.' },
      { title: 'Asia-Pacific & Latin America', description: 'Singapore, Malaysia, Brazil, Chile, and others have published phased ISSB adoption roadmaps through 2027-2030.' },
    ],
    footer: 'As of April 2026: 28 jurisdictions have adopted ISSB standards; a further 12 are planning adoption.',
  },
  {
    id: 38, layout: 'keyterm', module: 4,
    kicker: 'PART 3', term: 'Extraterritorial Reach of CSRD',
    definition: "A frequent misunderstanding — CSRD does not stop at the EU's borders.",
    body: 'A non-EU parent company can fall within CSRD scope if its EU subsidiary or branch meets size thresholds. Net turnover generated in the EU above the relevant threshold (currently under revision through the 2026 simplification process) can trigger group-level reporting obligations. Even where the parent itself is not in scope, EU subsidiaries may need to report at the subsidiary level. Always reassess scope after M&A activity, market entry, or significant revenue growth in the EU.',
    templateAsset: 'problem-solving',
    callouts: [
      { label: 'Practitioner tip', text: 'Build CSRD scope reassessment into your annual legal entity review, not just your sustainability calendar.' },
    ],
  },
  {
    id: 39, layout: 'exercise', module: 4,
    kicker: 'WORKSHOP EXERCISE', meta: '15 min', title: 'Exercise: Map Your Jurisdictional Exposure',
    steps: [
      { title: 'List every jurisdiction', detail: 'Every jurisdiction your organization operates, sells, borrows, or lists in.' },
      { title: 'Note the activity type', detail: 'What ties you to that jurisdiction — operating, selling, borrowing, or listing.' },
      { title: 'Note the current obligation', detail: 'The current ESG disclosure obligation, if any, in that jurisdiction.' },
      { title: 'Note the upcoming change', detail: 'Any known upcoming change to that obligation.' },
    ],
    checkFor: 'a jurisdiction-by-jurisdiction table covering activity type, current obligation, and upcoming change for every jurisdiction you operate, sell, borrow, or list in.',
  },

  // ---------- Part 4: Company Application ----------
  {
    id: 40, layout: 'intro', module: 5,
    kicker: 'PART 4', title: 'Bringing It to Company Level',
    lead: 'Applying materiality, governance, and target-setting to a specific organization — the core skill this session builds.',
    outcome: 'A repeatable five-step process for turning frameworks into an operating ESG programme.',
    templateAsset: 'action-communication', imageChrome: 'none',
  },
  {
    id: 41, layout: 'timeline', module: 5,
    kicker: 'PART 4', title: 'From Global Concept to Company Practice',
    lead: 'A repeatable five-step process for translating frameworks into an operating ESG program. We will work through each step using a generic illustrative company as a running example.',
    steps: [
      { era: 'Step 1', title: 'Scope & Applicability', description: 'Before assessing materiality, establish which regulations and frameworks actually apply.' },
      { era: 'Step 2', title: 'Materiality Assessment', description: 'A structured, repeatable process — not a one-time checkbox exercise.' },
      { era: 'Step 3', title: 'Governance Design', description: 'Credible disclosure requires a governance structure that an auditor or regulator can clearly trace.' },
      { era: 'Step 4', title: 'Target & KPI Setting', description: 'Targets should be specific, time-bound, and tied to a verifiable baseline.' },
      { era: 'Step 5', title: 'Disclosure Planning', description: 'Translate your materiality, governance, and KPI work into the actual disclosure document and process.' },
    ],
  },
  {
    id: 42, layout: 'numbered', module: 5,
    kicker: 'PART 4 · STEP 1', title: 'Determining Scope & Applicability',
    lead: 'Before assessing materiality, establish which regulations and frameworks actually apply.',
    items: [
      { title: 'Where are we listed or headquartered?', description: 'Determines exposure to mandatory regimes (e.g. CSRD, SEC climate rules, national ISSB adoption).' },
      { title: 'What size and revenue thresholds do we meet?', description: 'Many regimes set headcount, turnover, or balance sheet thresholds for applicability.' },
      { title: 'Do major customers or financiers require disclosure?', description: 'Contractual requirements can trigger voluntary-but-essential reporting even without legal mandate.' },
      { title: 'Do we have subsidiaries or operations triggering other regimes?', description: 'A non-EU parent with sufficient EU revenue can still fall within CSRD scope.' },
    ],
  },
  {
    id: 43, layout: 'numbered', module: 5,
    kicker: 'PART 4 · STEP 2', title: 'Conducting a Materiality Assessment',
    lead: 'A structured, repeatable process — not a one-time checkbox exercise.',
    items: [
      { title: 'Identify a long-list of topics', description: 'Use framework topic lists (GRI Universal/Sector Standards, SASB industry standards) as a starting point.' },
      { title: 'Engage stakeholders', description: 'Surveys, interviews, or workshops with the groups identified earlier — internal and external.' },
      { title: 'Score each topic', description: 'Rate financial significance and impact significance, typically on a 1-5 or 1-10 scale.' },
      { title: 'Plot and validate', description: 'Build the materiality matrix; validate findings with leadership and subject-matter experts.' },
      { title: 'Document and review periodically', description: 'Materiality shifts — reassess at least every 1-2 years or after major business changes.' },
    ],
  },
  {
    id: 44, layout: 'exercise', module: 5,
    kicker: 'WORKSHOP EXERCISE', meta: '10 min · in pairs', title: 'Exercise: Scope a Fictional Company',
    steps: [
      { title: 'Headquarters', detail: 'Headquartered outside the EU, mid-cap industrial manufacturer.' },
      { title: 'Scale', detail: '1,800 employees globally; €420M annual revenue.' },
      { title: 'EU exposure', detail: '30% of revenue from EU-based customers via a German subsidiary.' },
      { title: 'Listing', detail: 'Listed on a domestic stock exchange that has announced ISSB alignment for 2027.' },
      { title: 'Customer requirement', detail: 'Largest customer (a global automaker) requires Scope 1-3 emissions data annually.' },
    ],
    checkFor: 'Discuss: Is this company likely in CSRD scope? What about ISSB? What should it prioritize first?',
  },
  {
    id: 45, layout: 'cards', module: 5,
    kicker: 'PART 4 · STEP 3', title: 'Designing ESG Governance',
    lead: 'Credible disclosure requires a governance structure that an auditor or regulator can clearly trace.',
    columns: 5,
    cards: [
      { title: 'Board / Audit Committee', description: 'Approves ESG strategy and disclosures; oversees risk; reviews assurance results.' },
      { title: 'Executive Sponsor', description: 'Owns ESG strategy execution; reports to the board; resolves cross-functional blockers.' },
      { title: 'ESG Working Group', description: 'Cross-functional team coordinating data collection and topic ownership.' },
      { title: 'Data & Systems Owner', description: 'Maintains the underlying data infrastructure and internal controls.' },
      { title: 'Internal Audit', description: 'Tests controls ahead of external assurance; flags data quality issues early.' },
    ],
  },
  {
    id: 46, layout: 'cards', module: 5,
    kicker: 'PART 4', title: 'Common Governance Pitfalls',
    lead: 'Patterns practitioners repeatedly see in early-stage ESG programs.',
    columns: 2,
    cards: [
      { title: 'Single-owner risk', description: 'ESG sits with one person who lacks authority to compel data from business units.' },
      { title: 'No board reporting line', description: 'ESG strategy is set without board visibility, creating disclosure and oversight gaps.' },
      { title: 'No escalation path', description: 'Data quality issues surface only at year-end, with no time to remediate before disclosure.' },
      { title: 'Disconnected from enterprise risk', description: "ESG risk register sits separately from the company's main risk management process." },
    ],
  },
  {
    id: 47, layout: 'numbered', module: 5,
    kicker: 'PART 4 · STEP 4', title: 'Setting Credible Targets & KPIs',
    lead: 'Targets should be specific, time-bound, and tied to a verifiable baseline.',
    items: [
      { title: 'Baseline first', description: 'Establish a verified baseline year before setting any reduction or improvement target.' },
      { title: 'Specific & time-bound', description: '"Reduce Scope 1+2 emissions 30% by 2030 vs. 2023 baseline" — not vague aspirations.' },
      { title: 'Tied to a recognized methodology', description: 'Where possible, align targets to frameworks like the Science Based Targets initiative.' },
      { title: 'Owned and tracked', description: 'Each target needs a named owner and a regular review cadence (quarterly is common).' },
    ],
  },
  {
    id: 48, layout: 'comparison', module: 5,
    kicker: 'PART 4', title: 'Illustrative KPI Library by Pillar',
    lead: 'A starting reference — actual KPIs should be filtered through your materiality assessment.',
    columns: [
      { heading: 'Environmental', items: [
        'Scope 1, 2, 3 GHG emissions (tCO2e)',
        'Energy consumption & renewable %',
        'Water withdrawal & discharge',
        'Waste generated & diverted from landfill',
      ] },
      { heading: 'Social', items: [
        'Employee turnover rate',
        'Lost-time injury frequency rate',
        'Gender pay gap',
        '% of supply chain audited for labor standards',
      ] },
      { heading: 'Governance', items: [
        '% independent board members',
        'Board ESG expertise / oversight frequency',
        'Anti-corruption training completion rate',
        'Whistleblower cases resolved',
      ] },
    ],
  },
  {
    id: 49, layout: 'numbered', module: 5,
    kicker: 'PART 4 · STEP 5', title: 'Planning the Disclosure Itself',
    lead: 'Translate your materiality, governance, and KPI work into the actual disclosure document and process.',
    items: [
      { title: 'Map to disclosure requirements', description: 'Map material topics to specific framework disclosure requirements (e.g. ESRS data points, IFRS S2 metrics).' },
      { title: 'Decide the disclosure vehicle', description: 'Standalone sustainability report, integrated annual report, or regulatory filing.' },
      { title: 'Build an internal sign-off process', description: 'Mirroring financial reporting controls.' },
      { title: 'Plan assurance scope and timeline', description: 'Limited assurance is the common starting point.' },
      { title: 'Establish a disclosure calendar', description: 'Aligned to financial reporting deadlines.' },
    ],
  },

  // ---------- Part 5: Technical Implementation ----------
  {
    id: 50, layout: 'intro', module: 6,
    kicker: 'PART 5', title: 'Implementation: Data, Assurance & Roadmap',
    lead: 'The operational backbone that makes ongoing disclosure sustainable rather than a yearly fire drill.',
    outcome: 'GHG accounting mechanics, supply chain due diligence, and how to sequence a 12-month roadmap.',
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 51, layout: 'numbered', module: 6,
    kicker: 'PART 5', title: 'Building ESG Data Infrastructure',
    lead: 'Most ESG programs stall not on strategy, but on the difficulty of getting reliable, auditable data.',
    items: [
      { title: 'Centralize data sources', description: 'Avoid disparate spreadsheets — use a system of record (dedicated ESG software or integrated finance modules).' },
      { title: 'Map data owners', description: 'Each metric needs a single accountable owner, ideally embedded in existing operational reporting.' },
      { title: 'Build in validation checks', description: 'Range checks, year-over-year variance flags, and unit consistency checks reduce error rates.' },
      { title: 'Document methodology', description: 'Calculation methodologies must be documented clearly enough for an external assurer to follow.' },
    ],
  },
  {
    id: 52, layout: 'comparison', module: 6,
    kicker: 'PART 5', title: 'Special Topic: The Scope 3 Data Challenge',
    lead: "Scope 3 emissions are typically 70%+ of a company's footprint, yet the hardest data to obtain reliably.",
    columns: [
      { heading: 'Common approaches', items: [
        'Spend-based estimation (lowest accuracy, fastest to start)',
        'Industry-average emission factors per activity',
        'Supplier-specific data (highest accuracy, hardest to collect)',
        'Hybrid approach, improving accuracy of top suppliers over time',
      ] },
      { heading: 'Practical guidance', items: [
        'Start with spend-based estimates to establish a baseline quickly.',
        'Prioritize supplier engagement for your top 20% of spend (often 80% of impact).',
        'ISSB amendments (Dec 2025) aim to ease GHG disclosure complexity — track applicability.',
        'Be transparent about methodology and estimation level in your disclosure.',
      ] },
    ],
  },
  {
    id: 53, layout: 'comparison', module: 6,
    kicker: 'PART 5', title: 'Assurance Readiness',
    lead: 'Many mandatory regimes now require independent assurance over ESG disclosures, starting with limited assurance.',
    columns: [
      { heading: 'Limited Assurance', items: ['Auditor performs inquiries and analytical procedures; concludes "nothing has come to our attention" — a lower bar than financial audit.'] },
      { heading: 'Reasonable Assurance', items: ['Equivalent rigor to a financial statement audit; auditor expresses a positive opinion on the disclosures.'] },
    ],
    callout: { label: 'Readiness checklist', text: 'Documented methodologies for each material metric · Evidence trails (source documents, calculations) retained · Internal review/sign-off completed before external assurance begins.' },
  },
  {
    id: 54, layout: 'numbered', module: 6,
    kicker: 'PART 5 · RISK', title: 'Avoiding Greenwashing Risk',
    lead: 'Regulatory and reputational scrutiny of unsubstantiated ESG claims has intensified sharply.',
    items: [
      { title: 'Evidence every claim', description: 'Only make claims you can evidence with verifiable, current data.' },
      { title: 'Avoid vague terms', description: 'Avoid vague terms ("eco-friendly," "green") without specific, substantiated meaning.' },
      { title: 'Disclose methodology', description: 'Disclose methodology and assumptions, not just the headline number.' },
      { title: 'Keep claims current', description: "Update claims when underlying data or targets change — don't let claims go stale." },
    ],
  },
  {
    id: 55, layout: 'exercise', module: 6,
    kicker: 'WORKSHOP EXERCISE', meta: '20 min · in groups', title: 'Capstone Exercise: Build a 12-Month Roadmap',
    steps: [
      { title: 'Scoping conclusion', detail: 'What is your scoping conclusion — which framework(s) apply, and by when?' },
      { title: 'Top material topics', detail: 'What are your top 5 material topics, and how confident are you in that assessment?' },
      { title: 'Governance structure', detail: 'What governance structure will you propose, and who is the executive sponsor?' },
      { title: 'Quick wins', detail: 'What are 2-3 realistic quick wins within the next 2 quarters?' },
      { title: 'Biggest data gap', detail: "What is your single biggest data gap, and what's the first step to closing it?" },
    ],
    checkFor: 'using everything covered today, a roadmap for your own organization (or the exercise company).',
  },
  {
    id: 56, layout: 'timeline', module: 6,
    kicker: 'PART 5', title: 'A Generic 12-Month Implementation Roadmap',
    lead: 'Use this as a starting template — actual timing depends on regulatory deadlines and starting maturity.',
    steps: [
      { era: 'Q1', title: 'Scope & gap assessment', description: 'Stakeholder engagement begins. Governance charter drafted.' },
      { era: 'Q2', title: 'Materiality assessment completed', description: 'Data infrastructure selection. KPI library finalized.' },
      { era: 'Q3', title: 'Data collection & validation', description: 'Target-setting workshops. Draft disclosure outline.' },
      { era: 'Q4', title: 'Internal review & sign-off', description: 'Assurance scoping. First disclosure published.' },
    ],
  },
  {
    id: 57, layout: 'numbered', module: 6,
    kicker: 'PART 5', title: 'GHG Accounting Mechanics',
    lead: 'The core calculation logic behind every emissions disclosure — understanding this prevents common audit findings.',
    items: [
      { title: 'Activity data × emission factor', description: 'The basic formula: a quantity of activity (fuel burned, electricity used) multiplied by a factor converting it to CO2-equivalent.' },
      { title: 'Organizational boundary', description: "Choose equity share, financial control, or operational control — this determines which entities' emissions you consolidate." },
      { title: 'Operational boundary', description: 'Defines which Scope 1, 2, and 3 categories are included — should align with your organizational boundary choice.' },
      { title: 'GHG Protocol as the basis', description: 'Almost all frameworks (ISSB, CSRD, GRI) reference the GHG Protocol Corporate Standard as the underlying methodology.' },
    ],
  },
  {
    id: 58, layout: 'numbered', module: 6,
    kicker: 'PART 5', title: 'Worked Example: Scope 1 Calculation',
    lead: 'Scenario: a facility burns 50,000 liters of diesel for backup generators in one year. Always use the most current published emission factors (e.g. from DEFRA, EPA, or IPCC) and disclose the source.',
    items: [
      { title: 'Step 1 — Activity data', description: '50,000 liters of diesel consumed' },
      { title: 'Step 2 — Emission factor', description: '≈2.68 kg CO2e per liter of diesel (illustrative factor; use current published factors)' },
      { title: 'Step 3 — Calculation', description: '50,000 × 2.68 kg = 134,000 kg CO2e = 134 tCO2e' },
      { title: 'Step 4 — Classification', description: 'Reported as Scope 1 (direct emissions from owned equipment)' },
    ],
  },
  {
    id: 59, layout: 'numbered', module: 6,
    kicker: 'PART 5', title: 'Supply Chain Due Diligence',
    lead: 'Human rights and environmental due diligence obligations increasingly extend ESG accountability into the supply chain.',
    items: [
      { title: 'Map your supply chain', description: 'Map by tier, prioritizing by spend and risk (geography, sector, labor practices).' },
      { title: 'Issue supplier codes of conduct', description: 'Covering labor, environmental, and anti-corruption standards.' },
      { title: 'Build an audit and verification cadence', description: 'For high-risk suppliers, with remediation plans for findings.' },
      { title: 'Maintain a grievance mechanism', description: 'Accessible to workers across the supply chain, not just direct employees.' },
      { title: 'Document due diligence processes', description: 'Increasingly a disclosure requirement in its own right (e.g. under CSRD/ESRS).' },
    ],
  },

  // ---------- Part 6: Assessment & Roadmap ----------
  {
    id: 60, layout: 'intro', module: 7,
    kicker: 'PART 6', title: 'Assessment & Final Roadmap',
    lead: "Consolidating the day's material with a structured knowledge check before the capstone planning exercise.",
    outcome: "A tested understanding of the day's material and a completed roadmap for your own organization.",
    templateAsset: 'take-action', imageChrome: 'none',
  },
  {
    id: 61, layout: 'numbered', module: 7,
    kicker: 'PART 6', title: 'Knowledge Check',
    lead: 'Test your understanding before the capstone exercise. Discuss answers as a group.',
    items: [
      { title: 'Which materiality basis does IFRS S1 use exclusively?', description: 'Financial materiality' },
      { title: 'What does "double materiality" combine?', description: 'Financial materiality and impact materiality' },
      { title: "Which body's recommendations are now fully integrated into IFRS S2?", description: 'TCFD' },
      { title: 'Approximately what share of global GDP is represented by ISSB-adopting jurisdictions (as of April 2026)?', description: 'More than 50%' },
      { title: "What is typically the largest share of a company's GHG footprint?", description: 'Scope 3 emissions' },
    ],
  },
  {
    id: 62, layout: 'cards', module: 7,
    kicker: 'REFERENCE', title: 'Quick-Reference Glossary',
    lead: 'Key terms used throughout this session.',
    columns: 3,
    cards: [
      { title: 'Materiality', description: 'The threshold for whether an ESG issue is significant enough to report or act on.' },
      { title: 'Double materiality', description: 'Considering both financial materiality and impact materiality together (CSRD).' },
      { title: 'Assurance', description: 'Independent verification of reported ESG data (limited or reasonable).' },
      { title: 'Scope 1 / 2 / 3', description: 'GHG emissions categories by source and control across the value chain.' },
      { title: 'Greenwashing', description: 'Making misleading or unsubstantiated claims about environmental performance.' },
      { title: 'Baseline year', description: 'The reference year against which future ESG performance is measured.' },
      { title: 'ESRS', description: 'European Sustainability Reporting Standards — the technical standards under CSRD.' },
      { title: 'GICS', description: "Global Industry Classification Standard — used to map SASB's industry-specific metrics." },
      { title: 'Organizational boundary', description: 'The consolidation approach (equity share, financial or operational control) for GHG accounting.' },
      { title: 'GHG Protocol', description: 'The widely-used methodology underpinning most corporate GHG accounting and reporting standards.' },
    ],
  },
  {
    id: 63, layout: 'outcome',
    kicker: 'Thank You', title: 'Key Takeaways',
    checks: [
      'Materiality — financial, impact, or double — is the filter that makes ESG reporting manageable.',
      "Frameworks are complementary, not competing; most companies use more than one, and jurisdictional detail determines what's mandatory.",
      'Governance, GHG accounting rigor, and supply chain due diligence determine whether disclosure is credible and assurance-ready.',
      'A phased roadmap, anchored in your specific materiality findings, turns strategy into execution.',
    ],
    footnote: "You've completed the comprehensive ESG deep dive. Use the roadmap and worksheets from today as the starting point for your organization's ESG program. Revisit your materiality assessment annually.",
  },
];
